/**
 * InboxPage
 *
 * Main email interface with three-panel layout:
 * - Left: FolderSidebar
 * - Center: MessageList (or SmartInboxView when smart_inbox enabled) with EmailToolbar
 * - Right: ReadingPane
 *
 * URL-driven folder/label selection. Keyboard shortcuts for navigation.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { useEmailAccounts, useMessages, useFolders, useLabels, useMessageActions } from '../hooks';
import { useEmailModules } from '../hooks/useEmailModules';
import { useSmartInbox } from '../hooks/useSmartInbox';
import { useSnooze } from '../hooks/useSnooze';
import { useScheduleSend } from '../hooks/useScheduleSend';
import { useScreener } from '../hooks/useScreener';
import { FolderSidebar, MessageList, EmailToolbar, ReadingPane } from '../components';
import { SmartInboxView } from '../components/SmartInboxView';
import { SnoozedView } from '../components/SnoozedView';
import { ScheduledView } from '../components/ScheduledView';
import { SnoozeDialog } from '../components/SnoozeDialog';
// SettingsDialog lives in SettingsPage.tsx today because it's tightly coupled
// with the per-tab settings components defined in that file. Import via the
// pages barrel so the rest of this file doesn't reach into a sibling page.
import { SettingsDialog } from '.';
import { AISidebar } from '../components/AISidebar';
import { CommandPalette } from '../components/CommandPalette';
import { useCommandPalette } from '../hooks/useCommandPalette';
import type { Command } from '../hooks/useCommandPalette';

export function InboxPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { folderId: urlFolderId, labelId: urlLabelId } = useParams<{
    folderId?: string;
    labelId?: string;
  }>();

  const {
    activeAccount,
    accounts,
    loading: accountLoading,
    fetchAccounts,
    selectAccount,
  } = useEmailAccounts();
  const accountId = activeAccount?.id;

  // Onboarding: create account if none exists
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const { toast } = useToast();

  const handleCreateAccount = useCallback(async () => {
    if (!newUsername.trim()) return;
    setCreatingAccount(true);
    try {
      await api.createEmailAccount({ username: newUsername.trim() });
      await fetchAccounts();
      toast({ title: 'Email account created!' });
    } catch (err) {
      toast({
        title: 'Failed to create account',
        description: err instanceof Error ? err.message : 'Try a different username',
        variant: 'destructive',
      });
    } finally {
      setCreatingAccount(false);
    }
  }, [newUsername, fetchAccounts, toast]);

  const { folders, loading: foldersLoading } = useFolders(accountId);
  const { labels, loading: labelsLoading } = useLabels(accountId);
  const { isModuleEnabled } = useEmailModules(accountId);

  const smartInboxEnabled = isModuleEnabled('smart_inbox');
  const schedulingEnabled = isModuleEnabled('scheduling');
  const screenerEnabled = isModuleEnabled('screener');

  const { snoozedCount } = useSnooze(accountId);
  const { scheduledCount } = useScheduleSend(accountId);
  const { pendingCount: screenerPendingCount } = useScreener(accountId);

  const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Determine active folder — default to inbox
  const inboxFolder = useMemo(() => folders.find((f) => f.systemRole === 'inbox'), [folders]);
  const activeFolderId = urlFolderId || inboxFolder?.id || null;
  const activeLabelId = urlLabelId || null;

  // Check for virtual folder views. Use the router-supplied pathname so
  // re-renders track navigation; reading window.location.pathname directly
  // would go stale until the next render cycle.
  const pathParts = location.pathname.split('/');
  const lastSegment = pathParts[pathParts.length - 1];
  const isSnoozedView = lastSegment === 'snoozed';
  const isScheduledView = lastSegment === 'scheduled';

  // Fetch messages for the active folder/label
  const {
    messages,
    loading: messagesLoading,
    refresh: refreshMessages,
  } = useMessages({
    accountId,
    folderId: activeLabelId ? undefined : activeFolderId || undefined,
    labelId: activeLabelId || undefined,
  });

  // Smart inbox data
  const smartInbox = useSmartInbox(
    smartInboxEnabled ? accountId : undefined,
    smartInboxEnabled ? activeFolderId || undefined : undefined,
  );

  const { toggleStar, archiveMessages, trashMessages, markRead, markUnread, starMessages } =
    useMessageActions(accountId);

  const { snooze } = useSnooze(accountId);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);

  // Clear selection when folder changes
  useEffect(() => {
    setSelectedIds(new Set());
    setActiveMessageId(null);
  }, [activeFolderId, activeLabelId]);

  const handleSelectMessage = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(new Set(messages.map((m) => m.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [messages],
  );

  const handleClickMessage = useCallback(
    (id: string) => {
      setActiveMessageId(id);
      const msg = messages.find((m) => m.id === id);
      if (msg && !msg.isRead && accountId) {
        markRead([id]);
      }
    },
    [messages, accountId, markRead],
  );

  const handleToggleStar = useCallback(
    (id: string) => {
      const msg = messages.find((m) => m.id === id);
      if (msg) {
        toggleStar(id, !msg.isStarred);
      }
    },
    [messages, toggleStar],
  );

  const handleSelectFolder = useCallback(
    (folderId: string) => {
      navigate(`/a/arche_email/folder/${folderId}`);
    },
    [navigate],
  );

  const handleSelectLabel = useCallback(
    (labelId: string) => {
      navigate(`/a/arche_email/label/${labelId}`);
    },
    [navigate],
  );

  const handleNavigate = useCallback(
    (path: string) => {
      if (path === 'settings') {
        setSettingsOpen(true);
      } else {
        navigate(`/a/arche_email/${path}`);
      }
    },
    [navigate],
  );

  const handleSnooze = useCallback(
    async (snoozeUntil: string) => {
      const targets =
        selectedIds.size > 0 ? Array.from(selectedIds) : activeMessageId ? [activeMessageId] : [];
      for (const id of targets) {
        await snooze(id, snoozeUntil);
      }
      setSnoozeDialogOpen(false);
    },
    [selectedIds, activeMessageId, snooze],
  );

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const currentIndex = activeMessageId
        ? messages.findIndex((m) => m.id === activeMessageId)
        : -1;

      switch (e.key) {
        case 'j':
          if (currentIndex < messages.length - 1) {
            handleClickMessage(messages[currentIndex + 1].id);
          }
          break;
        case 'k':
          if (currentIndex > 0) {
            handleClickMessage(messages[currentIndex - 1].id);
          }
          break;
        case 'e':
          if (selectedIds.size > 0) archiveMessages(selectedArray);
          else if (activeMessageId) archiveMessages([activeMessageId]);
          break;
        case '#':
          if (selectedIds.size > 0) trashMessages(selectedArray);
          else if (activeMessageId) trashMessages([activeMessageId]);
          break;
        case 's':
          if (activeMessageId) handleToggleStar(activeMessageId);
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    activeMessageId,
    messages,
    selectedIds,
    selectedArray,
    handleClickMessage,
    handleToggleStar,
    archiveMessages,
    trashMessages,
  ]);

  const loading = accountLoading || foldersLoading || labelsLoading;

  // Command palette commands
  const commands = useMemo<Command[]>(
    () => [
      {
        id: 'compose',
        label: 'Compose new email',
        category: 'compose',
        shortcut: 'c',
        action: () => {},
      },
      {
        id: 'search',
        label: 'Search emails',
        category: 'search',
        shortcut: '/',
        action: () => navigate('/a/arche_email/search'),
      },
      {
        id: 'inbox',
        label: 'Go to Inbox',
        category: 'navigate',
        shortcut: 'g i',
        action: () => navigate('/a/arche_email'),
      },
      {
        id: 'sent',
        label: 'Go to Sent',
        category: 'navigate',
        action: () => {
          const f = folders.find((x) => x.systemRole === 'sent');
          if (f) navigate(`/a/arche_email/folder/${f.id}`);
        },
      },
      {
        id: 'drafts',
        label: 'Go to Drafts',
        category: 'navigate',
        action: () => {
          const f = folders.find((x) => x.systemRole === 'drafts');
          if (f) navigate(`/a/arche_email/folder/${f.id}`);
        },
      },
      {
        id: 'settings',
        label: 'Settings',
        category: 'navigate',
        action: () => navigate('/a/arche_email/settings'),
      },
      {
        id: 'contacts',
        label: 'Contacts',
        category: 'navigate',
        action: () => navigate('/a/arche_email/contacts'),
      },
      {
        id: 'archive',
        label: 'Archive',
        category: 'action',
        shortcut: 'e',
        action: () => {
          if (activeMessageId) archiveMessages([activeMessageId]);
        },
      },
      {
        id: 'trash',
        label: 'Trash',
        category: 'action',
        shortcut: '#',
        action: () => {
          if (activeMessageId) trashMessages([activeMessageId]);
        },
      },
      {
        id: 'star',
        label: 'Star',
        category: 'action',
        shortcut: 's',
        action: () => {
          if (activeMessageId) handleToggleStar(activeMessageId);
        },
      },
      {
        id: 'mark-read',
        label: 'Mark read',
        category: 'action',
        action: () => {
          if (activeMessageId) markRead([activeMessageId]);
        },
      },
      {
        id: 'mark-unread',
        label: 'Mark unread',
        category: 'action',
        action: () => {
          if (activeMessageId) markUnread([activeMessageId]);
        },
      },
      {
        id: 'ai-sidebar',
        label: 'Open AI Assistant',
        category: 'ai',
        action: () => setAiSidebarOpen(true),
      },
    ],
    [
      navigate,
      folders,
      activeMessageId,
      archiveMessages,
      trashMessages,
      handleToggleStar,
      markRead,
      markUnread,
    ],
  );

  const commandPalette = useCommandPalette(commands);

  // Show onboarding if no account after loading
  if (!accountLoading && !accountId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="max-w-sm text-center space-y-4 p-6">
          <Mail className="h-16 w-16 mx-auto text-primary opacity-60" />
          <h2 className="text-xl font-semibold">Welcome to Email</h2>
          <p className="text-sm text-muted-foreground">
            Create your Fias email account to get started.
          </p>
          <div className="flex gap-2">
            <Input
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="username"
              className="text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateAccount();
              }}
            />
            <span className="flex items-center text-sm text-muted-foreground whitespace-nowrap">
              @fias.email
            </span>
          </div>
          <Button
            className="w-full"
            onClick={handleCreateAccount}
            disabled={creatingAccount || !newUsername.trim()}
          >
            {creatingAccount ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create Account
          </Button>
        </div>
      </div>
    );
  }

  // Determine center panel content
  const renderCenterContent = () => {
    if (isSnoozedView) {
      return <SnoozedView accountId={accountId} />;
    }
    if (isScheduledView) {
      return <ScheduledView accountId={accountId} />;
    }

    if (smartInboxEnabled && activeFolderId === inboxFolder?.id && !activeLabelId) {
      return (
        <SmartInboxView
          categories={smartInbox.categories}
          loading={smartInbox.loading}
          activeThreadId={activeMessageId}
          onClickThread={(id) => setActiveMessageId(id)}
        />
      );
    }

    return (
      <MessageList
        messages={messages}
        loading={loading || messagesLoading}
        selectedIds={selectedIds}
        activeMessageId={activeMessageId}
        onSelectMessage={handleSelectMessage}
        onSelectAll={handleSelectAll}
        onClickMessage={handleClickMessage}
        onToggleStar={handleToggleStar}
      />
    );
  };

  return (
    <div className="flex h-full">
      {/* Left panel: Folder sidebar */}
      <div className="w-56 flex-shrink-0 border-r border-border overflow-y-auto hidden md:block">
        <FolderSidebar
          folders={folders}
          labels={labels}
          activeFolderId={activeFolderId}
          activeLabelId={activeLabelId}
          accountId={accountId}
          activeAccount={activeAccount}
          accounts={accounts}
          onSelectAccount={selectAccount}
          snoozedCount={snoozedCount}
          scheduledCount={scheduledCount}
          screenerPendingCount={screenerPendingCount}
          schedulingEnabled={schedulingEnabled}
          screenerEnabled={screenerEnabled}
          onSelectFolder={handleSelectFolder}
          onSelectLabel={handleSelectLabel}
          onNavigate={handleNavigate}
        />
      </div>

      {/* Center panel: Message list */}
      <div className="flex flex-col flex-1 min-w-0 border-r border-border max-w-md">
        {!isSnoozedView && !isScheduledView && (
          <EmailToolbar
            selectedCount={selectedIds.size}
            schedulingEnabled={schedulingEnabled}
            onArchive={() => archiveMessages(selectedArray)}
            onTrash={() => trashMessages(selectedArray)}
            onMarkRead={() => markRead(selectedArray)}
            onMarkUnread={() => markUnread(selectedArray)}
            onStar={() => starMessages(selectedArray)}
            onSnooze={() => setSnoozeDialogOpen(true)}
            onRefresh={() => refreshMessages()}
          />
        )}
        {renderCenterContent()}
      </div>

      {/* Right panel: Reading pane */}
      <div className="flex-1 min-w-0 hidden lg:block">
        <ReadingPane messageId={activeMessageId} accountId={accountId} />
      </div>

      {/* AI Sidebar */}
      <AISidebar
        accountId={accountId}
        open={aiSidebarOpen}
        onClose={() => setAiSidebarOpen(false)}
      />

      {/* Snooze dialog */}
      <SnoozeDialog
        open={snoozeDialogOpen}
        onClose={() => setSnoozeDialogOpen(false)}
        onSnooze={handleSnooze}
      />

      {/* Settings dialog */}
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Command palette */}
      <CommandPalette
        open={commandPalette.open}
        query={commandPalette.query}
        onQueryChange={commandPalette.setQuery}
        commands={commandPalette.filtered}
        onExecute={commandPalette.execute}
        onClose={() => commandPalette.setOpen(false)}
      />
    </div>
  );
}
